import { Container, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, Checkbox, TextField, Select, MenuItem, Box, Typography, FormControl, InputLabel, Grid, Skeleton, InputAdornment, IconButton, useTheme, useMediaQuery, Card, CardContent, Stack } from "@mui/material";
import FilterListIcon from '@mui/icons-material/FilterList';
import React, { useEffect, useState, useCallback } from "react";
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete'; // Added import

import Accounts from '../../collections/accounts';
import OrdersCollection from '../../collections/orders';
import UsersCollection from '../../collections/users'; // Added import
import Account from '../../models/account';
import Order from '../../models/order';
import User from '../../models/user'; // Added import
import { excuseApi } from '../../commons/http';

// S13: order-level HDDT chip (shown only when an HDDT exists)
const HddtChip = ({ status, ...props }) => {
  if (!status || status === 'none') return null;
  const map = {
    draft: { label: 'HDDT nháp', bg: '#ede7f6', fg: '#5e35b1' },
    signed: { label: 'HDDT đã ký', bg: '#e8f5e9', fg: '#2e7d32' },
    adjusted: { label: 'HDDT điều chỉnh', bg: '#fff3e0', fg: '#e65100' },
  };
  const m = map[status] || { label: `HDDT ${status}`, bg: '#f5f5f5', fg: '#757575' };
  return <Chip label={m.label} sx={{ backgroundColor: m.bg, color: m.fg }} {...props} />;
};

const ListOrders = (props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return parseInt(params.get('page')) || 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return [
      params.get('start_date') ? dayjs(params.get('start_date')) : null, // Changed from startDate
      params.get('end_date') ? dayjs(params.get('end_date')) : null     // Changed from endDate
    ];
  });
  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('search') || "";
  });
  const [userNameSearch, setUserNameSearch] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('user_name') || "";
  });
  const [userNameInputValue, setUserNameInputValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('user_name') || "";
  });
  const [financialStatus, setFinancialStatus] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('status') || '';
  });
  const [totalOrders, setTotalOrders] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [createdByFilter, setCreatedByFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('created_by') || ''; // Changed from createdBy
  });
  const [loading, setLoading] = useState(true);

  // State variables for customer filter
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('customer_id') || ''; // Changed from customerId
  });
  const [initialCustomerObject, setInitialCustomerObject] = useState(null);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customersDropdownList, setCustomersDropdownList] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [initialCustomerLoadAttemptedForId, setInitialCustomerLoadAttemptedForId] = useState(null); // New state

  // New state for order status filter
  const [hddtStatusFilter, setHddtStatusFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('hddt_status') || '';
  });
  const [orderStatus, setOrderStatus] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('order_status') || '';
  });

  // New state for tags filter
  const [tagsFilter, setTagsFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tags') || '';
  });
  const [tagsInputValue, setTagsInputValue] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('tags') || '';
  });
  const [availableTags, setAvailableTags] = useState([]);

  // Cache for storing account data
  const accountCache = React.useRef(new Map());

  const resetAllFilters = () => {
    setDateRange([null, null]);
    setFinancialStatus('');
    setSearchTerm('');
    setInputValue(''); // Clear the main search input field
    setUserNameSearch('');
    setUserNameInputValue(''); // Clear the user name search input field
    setCreatedByFilter('');
    setSelectedCustomerId('');
    setInitialCustomerObject(null);
    setCustomerSearchInput(''); // Clear the customer search Autocomplete input
    setInitialCustomerLoadAttemptedForId(null);
    setOrderStatus(''); // Reset order status
    setHddtStatusFilter('');
    setTagsFilter(''); // Reset tags filter
    setTagsInputValue(''); // Clear tags input
    setPage(0);

    // Navigate to the base orders URL with page reset, clearing other query params
    const params = new URLSearchParams();
    params.set('page', '0');
    navigate(`?${params.toString()}`, { replace: true });
  };

  useEffect(() => {
    props.setPageTitle("Danh sách đơn hàng");

    const fetchAccounts = async () => {
      const accountList = new Accounts();
      await accountList.fetch();
      setAccounts(accountList.items);
    };

    fetchAccounts();
  }, [props]) // Reverted: Added props back to dependency array as per original structure

  // Fetch available tags
  useEffect(() => {
    const fetchTags = async () => {
      try {
        const res = await excuseApi({
          path: '/tags',
          method: 'GET'
        });
        if (res.status === 200) {
          setAvailableTags(res.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };
    fetchTags();
  }, []);

  const initialCustomer = useCallback(async () => {
    if (selectedCustomerId) {
      const user = new User();
      await user.getUserById(selectedCustomerId);
      console.log(user);
      if (user.id) {
        setInitialCustomerObject(user);
        setCustomerSearchInput(user.name || user.email || user.id || '');
        setCustomersDropdownList(prevList => {
          const existing = prevList.find(c => c.id === user.id);
          if (!existing) {
            return [user, ...prevList.filter(c => c.id !== user.id)];
          }
          return prevList;
        });
      } else {
        setInitialCustomerObject(null);
        setCustomerSearchInput('');
        setCustomersDropdownList([]);
      }
    } else {
      setInitialCustomerObject(null);
      setCustomerSearchInput('');
      setCustomersDropdownList([]);
    }
  }, [selectedCustomerId]);

  const fetchOrders = useCallback(async (page = 1, perPage = 10, filters = {}) => {
    setLoading(true);
    try {
      const ordersCollection = new OrdersCollection();
      let filterParams = {
        start_date: filters.startDate,
        end_date: filters.endDate,
        financial_status: filters.financialStatus,
        name: filters.searchTerm,
        user_name: filters.userNameSearch,
        user_id: filters.selectedCustomerId,
        status: filters.orderStatus, // <-- add this line
        hddt_status: filters.hddtStatus,
        tags: filters.tags, // <-- add tags filter
      };
      if (filters.createdBy) {
        filterParams.created_by = filters.createdBy;
      }
      // Remove undefined or empty filters
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key] === undefined || filterParams[key] === null || filterParams[key] === '') {
          delete filterParams[key];
        }
      });

      await ordersCollection.fetch(page, perPage, filterParams);

      // Get unique account IDs from the fetched orders (now Order model instances)
      const uniqueAccountIds = [...new Set((ordersCollection.items || []).map(order => order.created_by).filter(id => id != null))];

      // Fetch accounts data for unique IDs
      if (uniqueAccountIds.length > 0) {
        const account = new Account(); // Instantiate Account model
        await Promise.all(uniqueAccountIds.map(async (accountId) => {
          if (!accountCache.current.has(accountId)) {
            const accountData = await account.getAccountById(accountId);
            if (accountData) {
              accountCache.current.set(accountId, accountData);
            }
          }
        }));
      }

      // Map orders with account data from cache
      const ordersWithAccount = (ordersCollection.items || []).map(orderInstance => {
        const orderData = typeof orderInstance.toJSON === 'function' ? orderInstance.toJSON() : { ...orderInstance };
        return {
          ...orderData,
          created_by_name: orderInstance.created_by ? (accountCache.current.get(orderInstance.created_by)?.name || 'N/A') : 'N/A',
        };
      });

      setOrders(ordersWithAccount);
      setFilteredOrders(ordersWithAccount);
      setTotalOrders(ordersCollection.total);
    } catch (error) {
      console.error("Error fetching orders:", error);
      // Potentially set an error state here to display to the user
    }
    setLoading(false);
  }, []);

  // Debounce function
  const debounce = (func, delay) => {
    let timeout;
    return function(...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  // Function to fetch customers for the dropdown (driven by search input)
  const fetchCustomers = useCallback(async (localSearchTerm = '') => {
    if (!localSearchTerm || localSearchTerm.length < 2) {
      setCustomersDropdownList([]);
      setLoadingCustomers(false);
      return;
    }
    setLoadingCustomers(true);
    try {
      const customersCollection = new UsersCollection();
      // Pass role: 'customer' in params, and localSearchTerm as searchQuery (which maps to 'name')
      await customersCollection.fetch(1, 10, {}, localSearchTerm);
      setCustomersDropdownList(customersCollection.items || []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setCustomersDropdownList([]); // Clear list on error
    } finally {
      setLoadingCustomers(false);
    }
  }, []); // Dependencies: UsersCollection will be newed up, so no direct dependency.

  // Debounced version of fetchCustomers
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchCustomers = useCallback(debounce(fetchCustomers, 500), [fetchCustomers]);

  // Effect to load the specific customer object when selectedCustomerId changes (e.g., from URL or selection)
  useEffect(() => {
    // const loadInitialCustomerAsync = async () => { ... }; // Removed this inner function

    if (selectedCustomerId) {
      // The conditions for calling initialCustomer:
      // 1. initialCustomerObject is not set or its ID doesn't match selectedCustomerId.
      // 2. Not currently loadingCustomers.
      // The initialCustomer function itself might also have internal guards.
      if ((!initialCustomerObject || initialCustomerObject.id !== selectedCustomerId) && !loadingCustomers) {
        initialCustomer(); // Use the useCallback version
      }
      // The logic involving initialCustomerLoadAttemptedForId is assumed to be handled
      // within the initialCustomer callback or is implicitly managed by these conditions.
    } else { // No selectedCustomerId
      setInitialCustomerObject(null);
      // setCustomerSearchInput(''); // This line was previously commented, respecting that.
      setInitialCustomerLoadAttemptedForId(null); // Clear attempted ID
    }
  }, [selectedCustomerId, initialCustomerObject, loadingCustomers, initialCustomerLoadAttemptedForId, initialCustomer, setInitialCustomerObject, setInitialCustomerLoadAttemptedForId]); // Added initialCustomer and its own setters to dependencies

  // Effect to fetch customers for dropdown when search input changes
  useEffect(() => {
    if (customerSearchInput) {
      debouncedFetchCustomers(customerSearchInput);
    } else if (!selectedCustomerId && !customerSearchInput) {
      // Clear list only if search is empty AND no customer is actively selected.
      setCustomersDropdownList([]);
    }
    // If customerSearchInput is empty BUT selectedCustomerId is present,
    // the list will be managed by the first useEffect (to show the selected customer).
  }, [customerSearchInput, debouncedFetchCustomers, selectedCustomerId]);

  useEffect(() => {
    fetchOrders(page + 1, rowsPerPage, {
      startDate: dayjs(dateRange[0]).isValid() ? dayjs(dateRange[0]).format("YYYY-MM-DD") : undefined,
      endDate: dayjs(dateRange[1]).isValid() ? dayjs(dateRange[1]).format("YYYY-MM-DD") : undefined,
      financialStatus: financialStatus || undefined,
      searchTerm: searchTerm || undefined, // Maps to order name search as per docs/orders.yaml
      userNameSearch: userNameSearch || undefined, // Maps to user name search
      createdBy: createdByFilter || undefined,
      selectedCustomerId: selectedCustomerId || undefined, // Pass selectedCustomerId
      orderStatus: orderStatus || undefined, // <-- add this line
      hddtStatus: hddtStatusFilter || undefined,
      tags: tagsFilter || undefined, // <-- add tags filter
    });
  }, [dateRange, financialStatus, searchTerm, userNameSearch, page, rowsPerPage, createdByFilter, selectedCustomerId, orderStatus, hddtStatusFilter, tagsFilter, fetchOrders]); // Added tagsFilter

  const displayedOrders = filteredOrders;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    const params = new URLSearchParams(window.location.search);
    params.set('page', newPage);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    const params = new URLSearchParams(window.location.search);
    params.set('page', 0);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('vi-VN', options);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedOrders([]);
    } else {
      const allOrderIdsOnPage = displayedOrders.map(order => order.id);
      setSelectedOrders(allOrderIdsOnPage);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectOrder = (orderId) => {
    let updatedSelectedOrders;
    if (selectedOrders.includes(orderId)) {
      updatedSelectedOrders = selectedOrders.filter(id => id !== orderId);
    } else {
      updatedSelectedOrders = [...selectedOrders, orderId];
    }
    setSelectedOrders(updatedSelectedOrders);

    if (updatedSelectedOrders.length === displayedOrders.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  };

  const handleExportLabel = async () => {
    const labels = new Order();
    const res = await labels.exportLabel(selectedOrders);
    if (res.data) {
      const pdfBlob = new Blob([res.data], { type: 'application/pdf' });
      const downloadLink = window.URL.createObjectURL(pdfBlob);
      downloadPDF(downloadLink);
    }
  };

  const downloadPDF = (link) => {
    const now = new Date();
    const vietnamTime = new Intl.DateTimeFormat('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Ho_Chi_Minh'
    }).formatToParts(now);

    let dateParts = {};
    vietnamTime.forEach(({ type, value }) => {
      dateParts[type] = value;
    });

    const formattedTime = `${dateParts.hour}h${dateParts.minute}p${dateParts.second}s_${dateParts.day}-${dateParts.month}-${dateParts.year}`;

    const fileName = `${formattedTime}.pdf`;

    const linkElement = document.createElement('a');
    linkElement.href = link;
    linkElement.download = fileName;
    document.body.appendChild(linkElement);
    linkElement.click();
    document.body.removeChild(linkElement);
  };


  const handleDateChange = (index, newValue) => {
    const newDateRange = [...dateRange];
    newDateRange[index] = newValue;
    setDateRange(newDateRange);
    setPage(0);
    const params = new URLSearchParams(window.location.search);
    params.set('page', 0);
    if (index === 0) {
      if (newValue) {
        params.set('start_date', newValue.format('YYYY-MM-DD')); // Changed from startDate
      } else {
        params.delete('start_date'); // Changed from startDate
      }
    } else {
      if (newValue) {
        params.set('end_date', newValue.format('YYYY-MM-DD')); // Changed from endDate
      } else {
        params.delete('end_date'); // Changed from endDate
      }
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleSearch = () => {
    setSearchTerm(inputValue);
    setPage(0);
    const params = new URLSearchParams(window.location.search);
    params.set('page', 0);
    if (inputValue) {
      params.set('search', inputValue);
    } else {
      params.delete('search');
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleUserNameSearch = () => {
    setUserNameSearch(userNameInputValue);
    setPage(0);
    const params = new URLSearchParams(window.location.search);
    params.set('page', 0);
    if (userNameInputValue) {
      params.set('user_name', userNameInputValue);
    } else {
      params.delete('user_name');
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleTagsFilter = () => {
    setTagsFilter(tagsInputValue);
    setPage(0);
    const params = new URLSearchParams(window.location.search);
    params.set('page', 0);
    if (tagsInputValue) {
      params.set('tags', tagsInputValue);
    } else {
      params.delete('tags');
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  if (loading) {
    return (
      <Skeleton
        animation="wave"
        variant="rectangular"
        height={250}
        sx={{ m: 3 }}
      />
    );
  }

  return (
    <Container sx={{ mt: 3 }}>
      <Stack spacing={2} sx={{ marginBottom: '16px' }}>
        <Box sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          gap: 2
        }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/orders/new')}
            sx={{ textTransform: 'none' }}
            startIcon={<AddIcon />}
            fullWidth={isMobile}
          >
            Thêm đơn hàng mới
          </Button>

          {selectedOrders.length > 0 && (
            <Button
              variant="contained"
              onClick={handleExportLabel}
              sx={{ textTransform: 'none' }}
              fullWidth={isMobile}
            >
              Tải xuống đơn hàng
            </Button>
          )}
        </Box>

        <TextField
          label="Tìm kiếm mã đơn hàng"
          variant="outlined"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleSearch();
            }
          }}
          onBlur={handleSearch}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSearch}
                >
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label="Tìm kiếm tên hoặc SĐT khách hàng"
          variant="outlined"
          value={userNameInputValue}
          onChange={(e) => setUserNameInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleUserNameSearch();
            }
          }}
          onBlur={handleUserNameSearch}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleUserNameSearch}
                >
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Autocomplete
          freeSolo
          options={availableTags}
          inputValue={tagsInputValue}
          onInputChange={(event, newValue) => {
            setTagsInputValue(newValue);
          }}
          onChange={(event, newValue) => {
            if (newValue) {
              setTagsInputValue(newValue);
              handleTagsFilter();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleTagsFilter();
            }
          }}
          onBlur={handleTagsFilter}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label="Lọc theo tags"
              variant="outlined"
              placeholder="VD: VIP, Urgent"
            />
          )}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <Chip
                  label={option}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </li>
            );
          }}
        />
      </Stack>

      <Box sx={{ mb: 2 }}>
        {isMobile ? (
          <Box>
            <Button
              onClick={() => {
                const dialog = document.getElementById('filter-dialog');
                if (dialog) {
                  dialog.style.display = dialog.style.display === 'none' ? 'block' : 'none';
                }
              }}
              variant="outlined"
              fullWidth
              sx={{ mb: 2 }}
              endIcon={<FilterListIcon />}
            >
              Bộ lọc
            </Button>
            <Box
              id="filter-dialog"
              sx={{
                display: 'none',
                mb: 2
              }}
            >
              <Stack spacing={2}>
                <Button
                  onClick={resetAllFilters}
                  variant="text"
                  size="small"
                  color="secondary"
                  sx={{ alignSelf: 'flex-end', mt: -1, mb: 1 }} // Adjusted margin for better placement
                >
                  Xóa bộ lọc
                </Button>
                <DatePicker
                  label="Từ ngày"
                  value={dateRange[0]}
                  onChange={(newValue) => {
                    handleDateChange(0, newValue);
                    setPage(0);
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  inputFormat="DD/MM/YYYY"
                />
                <DatePicker
                  label="Đến ngày"
                  value={dateRange[1]}
                  onChange={(newValue) => {
                    handleDateChange(1, newValue);
                    setPage(0);
                  }}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                  inputFormat="DD/MM/YYYY"
                />
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="created-by-label" shrink>Người tạo đơn</InputLabel>
                  <Select
                    labelId="created-by-label"
                    value={createdByFilter}
                    onChange={(e) => {
                      setCreatedByFilter(e.target.value);
                      setPage(0);
                      const params = new URLSearchParams(window.location.search);
                      params.set('page', 0);
                      if (e.target.value) {
                        params.set('created_by', e.target.value); // Changed from createdBy
                      } else {
                        params.delete('created_by'); // Changed from createdBy
                      }
                      navigate(`?${params.toString()}`, { replace: true });
                    }}
                    label="Người tạo đơn"
                    displayEmpty
                  >
                    <MenuItem value="">Tất cả</MenuItem>
                    {accounts.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="financial-status-label" shrink>Trạng thái thanh toán</InputLabel>
                  <Select
                    labelId="financial-status-label"
                    value={financialStatus}
                    onChange={(e) => {
                      setFinancialStatus(e.target.value);
                      setPage(0);
                      const params = new URLSearchParams(window.location.search);
                      params.set('page', 0);
                      if (e.target.value) {
                        params.set('status', e.target.value);
                      } else {
                        params.delete('status');
                      }
                      navigate(`?${params.toString()}`, { replace: true });
                    }}
                    label="Trạng thái thanh toán"
                    displayEmpty
                  >
                    <MenuItem value="">Tất cả</MenuItem>
                    <MenuItem value="PAID">Đã thanh toán</MenuItem>
                    <MenuItem value="SKIPPED">Bỏ qua</MenuItem>
                    <MenuItem value="UNPAID">Chưa thanh toán</MenuItem>
                    <MenuItem value="MAPPED_BANK_TRANSACTION">Đã ghép giao dịch</MenuItem>
                  </Select>
                </FormControl>
                {/* New filter for order status - Mobile */}
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="order-status-label" shrink>Trạng thái đơn hàng</InputLabel>
                  <Select
                    labelId="order-status-label"
                    value={orderStatus}
                    onChange={(e) => {
                      setOrderStatus(e.target.value);
                      setPage(0);
                      const params = new URLSearchParams(window.location.search);
                      params.set('page', 0);
                      if (e.target.value) {
                        params.set('order_status', e.target.value);
                      } else {
                        params.delete('order_status');
                      }
                      navigate(`?${params.toString()}`, { replace: true });
                    }}
                    label="Trạng thái đơn hàng"
                    displayEmpty
                  >
                    <MenuItem value="">Tất cả</MenuItem>
                    <MenuItem value="CONFIRMING">Chờ xác nhận</MenuItem>
                    <MenuItem value="OPEN">Mới</MenuItem>
                    <MenuItem value="PACKAGED">Đã đóng gói</MenuItem>
                    <MenuItem value="SENT_INVOICE">Đã gửi hóa đơn</MenuItem>
                    <MenuItem value="CANCELLED">Đã hủy</MenuItem>
                  </Select>
                </FormControl>
                {/* Customer Autocomplete Filter - Mobile */}
                <Autocomplete
                  options={customersDropdownList}
                  getOptionLabel={(option) => option ? option.name || '' : ''}
                  value={initialCustomerObject || null} // Use initialCustomerObject
                  inputValue={customerSearchInput}
                  onInputChange={(event, newInputValue) => {
                    setCustomerSearchInput(newInputValue);
                  }}
                  onChange={(event, newValue) => { // newValue is an object or null
                    setSelectedCustomerId(newValue ? newValue.id : '');
                    setInitialCustomerObject(newValue); // Set the full object
                    setPage(0);
                    const params = new URLSearchParams(window.location.search);
                    params.set('page', '0');
                    if (newValue && newValue.id) {
                      params.set('customer_id', newValue.id); // Changed from customerId
                    } else {
                      params.delete('customer_id'); // Changed from customerId
                    }
                    navigate(`?${params.toString()}`, { replace: true });
                  }}
                  loading={loadingCustomers}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Khách hàng"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                  sx={{ mt: 2 }} // Added margin for spacing
                />
              </Stack>
            </Box>
            {/* Show active filters */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {userNameSearch && (
                <Chip
                  label={`Tên KH: ${userNameSearch}`}
                  onDelete={() => {
                    setUserNameSearch('');
                    setUserNameInputValue('');
                    const params = new URLSearchParams(window.location.search);
                    params.set('page', '0');
                    params.delete('user_name');
                    navigate(`?${params.toString()}`, { replace: true });
                  }}
                  size="small"
                />
              )}
              {dateRange[0] && (
                <Chip
                  label={`Từ: ${dayjs(dateRange[0]).format('DD/MM/YYYY')}`}
                  onDelete={() => handleDateChange(0, null)}
                  size="small"
                />
              )}
              {dateRange[1] && (
                <Chip
                  label={`Đến: ${dayjs(dateRange[1]).format('DD/MM/YYYY')}`}
                  onDelete={() => handleDateChange(1, null)}
                  size="small"
                />
              )}
              {createdByFilter && (
                <Chip
                  label={`Người tạo: ${accounts.find(a => a.id === createdByFilter)?.name || ''}`}
                  onDelete={() => {
                      setCreatedByFilter('');
                      const params = new URLSearchParams(window.location.search);
                      params.set('page', '0');
                      params.delete('created_by'); // Changed from createdBy
                      navigate(`?${params.toString()}`, { replace: true });
                  }}
                  size="small"
                />
              )}
              {financialStatus && (
                <Chip
                  label={`Trạng thái: ${financialStatus === 'PAID' ? 'Đã thanh toán' :
                    financialStatus === 'UNPAID' ? 'Chưa thanh toán' :
                      financialStatus === 'SKIPPED' ? 'Bỏ qua' : 
                      financialStatus === 'MAPPED_BANK_TRANSACTION' ? 'Đã ghép GD ngân hàng' : ''
                    }`}
                  onDelete={() => {
                      setFinancialStatus('');
                      const params = new URLSearchParams(window.location.search);
                      params.set('page', '0');
                      params.delete('status');
                      navigate(`?${params.toString()}`, { replace: true });
                  }}
                  size="small"
                />
              )}
              {initialCustomerObject && ( // Display chip if a customer is selected
                <Chip
                  label={`Khách hàng: ${initialCustomerObject.name || ''}`}
                  onDelete={() => {
                    setSelectedCustomerId('');
                    setInitialCustomerObject(null);
                    setCustomerSearchInput(''); // Clear search input as well
                    setPage(0);
                    const params = new URLSearchParams(window.location.search);
                    params.set('page', '0');
                    params.delete('customer_id'); // Changed from customerId
                    navigate(`?${params.toString()}`, { replace: true });
                  }}
                  size="small"
                />
              )}
              {orderStatus && (
                <Chip
                  label={`Trạng thái đơn: ${
                    orderStatus === 'CONFIRMING' ? 'Chờ xác nhận' :
                    orderStatus === 'OPEN' ? 'Mới' :
                    orderStatus === 'PACKAGED' ? 'Đã đóng gói' :
                    orderStatus === 'SENT_INVOICE' ? 'Đã gửi hóa đơn' :
                    orderStatus === 'CANCELLED' ? 'Đã hủy' :
                    orderStatus
                  }`}
                  onDelete={() => {
                    setOrderStatus('');
                    const params = new URLSearchParams(window.location.search);
                    params.set('page', '0');
                    params.delete('order_status');
                    navigate(`?${params.toString()}`, { replace: true });
              {hddtStatusFilter && (
                <Chip
                  label={`HDDT: ${hddtStatusFilter === 'none' ? 'Chưa có' :
                    hddtStatusFilter === 'draft' ? 'Nháp' :
                    hddtStatusFilter === 'signed' ? 'Đã ký' : 'Điều chỉnh'}`}
                  onDelete={() => {
                    setHddtStatusFilter('');
                    const params = new URLSearchParams(window.location.search);
                    params.delete('hddt_status');
                    navigate(`?${params.toString()}`, { replace: true });
                  }}
                  size="small"
                />
              )}
                  }}
                  size="small"
                />
              )}
              {tagsFilter && (
                <Chip
                  label={`Tags: ${tagsFilter}`}
                  onDelete={() => {
                    setTagsFilter('');
                    setTagsInputValue('');
                    const params = new URLSearchParams(window.location.search);
                    params.set('page', '0');
                    params.delete('tags');
                    navigate(`?${params.toString()}`, { replace: true });
                  }}
                  size="small"
                />
              )}
            </Box>
          </Box>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Từ ngày"
                value={dateRange[0]}
                onChange={(newValue) => {
                  handleDateChange(0, newValue);
                  setPage(0);
                }}
                renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                inputFormat="DD/MM/YYYY"
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}> {/* Adjusted grid size */}
              <DatePicker
                label="Đến ngày"
                value={dateRange[1]}
                onChange={(newValue) => {
                  handleDateChange(1, newValue);
                  setPage(0);
                }}
                renderInput={(params) => <TextField {...params} sx={{ width: '100%' }} />}
                inputFormat="DD/MM/YYYY"
                sx={{ width: '100%' }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}> {/* Adjusted grid size */}
              <FormControl variant="outlined" sx={{ width: '100%' }}>
                <InputLabel id="created-by-label" shrink>Người tạo đơn</InputLabel>
                <Select
                  labelId="created-by-label"
                  value={createdByFilter}
                  onChange={(e) => {
                    setCreatedByFilter(e.target.value);
                    setPage(0);
                    const params = new URLSearchParams(window.location.search);
                    params.set('page', 0);
                    if (e.target.value) {
                      params.set('created_by', e.target.value); // Changed from createdBy
                    } else {
                      params.delete('created_by'); // Changed from createdBy
                    }
                    navigate(`?${params.toString()}`, { replace: true });
                  }}
                  label="Người tạo đơn"
                  displayEmpty
                >
                  <MenuItem value="">Tất cả</MenuItem>
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}> {/* Adjusted grid size */}
              <FormControl variant="outlined" sx={{ width: '100%' }}>
                <InputLabel id="financial-status-label" shrink>Trạng thái thanh toán</InputLabel>
                <Select
                  labelId="financial-status-label"
                  value={financialStatus}
                  onChange={(e) => {
                    setFinancialStatus(e.target.value);
                    setPage(0);
                    const params = new URLSearchParams(window.location.search);
                    params.set('page', 0);
                    if (e.target.value) {
                      params.set('status', e.target.value);
                    } else {
                      params.delete('status');
                    }
                    navigate(`?${params.toString()}`, { replace: true });
                  }}
                  label="Trạng thái thanh toán"
                  displayEmpty
                >
                  <MenuItem value="">Tất cả</MenuItem>
                  <MenuItem value="PAID">Đã thanh toán</MenuItem>
                  <MenuItem value="SKIPPED">Bỏ qua</MenuItem>
                  <MenuItem value="UNPAID">Chưa thanh toán</MenuItem>
                  <MenuItem value="MAPPED_BANK_TRANSACTION">Đã ghép giao dịch</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* New filter for order status - Desktop */}
            <Grid item xs={12} sm={12} md={3}> {/* Adjusted grid size */}
              <FormControl variant="outlined" sx={{ width: '100%' }}>
                <InputLabel id="order-status-label" shrink>Trạng thái đơn hàng</InputLabel>
                <Select
                  labelId="order-status-label"
                  value={orderStatus}
                  onChange={(e) => {
                    setOrderStatus(e.target.value);
                    setPage(0);
                    const params = new URLSearchParams(window.location.search);
                    params.set('page', 0);
                    if (e.target.value) {
                      params.set('order_status', e.target.value);
                    } else {
                      params.delete('order_status');
                    }
                    navigate(`?${params.toString()}`, { replace: true });
                  }}
                  label="Trạng thái đơn hàng"
                  displayEmpty
                >
                  <MenuItem value="">Tất cả</MenuItem>
                  <MenuItem value="CONFIRMING">Chờ xác nhận</MenuItem>
                  <MenuItem value="OPEN">Mới</MenuItem>
                  <MenuItem value="PACKAGED">Đã đóng gói</MenuItem>
                  <MenuItem value="SENT_INVOICE">Đã gửi hóa đơn</MenuItem>
                  <MenuItem value="CANCELLED">Đã hủy</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {/* S13: filter by the order's HDDT state */}
            <Grid item xs={12} sm={12} md={3}>
              <FormControl variant="outlined" sx={{ width: '100%' }}>
                <InputLabel id="hddt-status-label" shrink>Hóa đơn điện tử</InputLabel>
                <Select
                  labelId="hddt-status-label"
                  value={hddtStatusFilter}
                  onChange={(e) => {
                    setHddtStatusFilter(e.target.value);
                    setPage(0);
                    const params = new URLSearchParams(window.location.search);
                    params.set('page', 0);
                    if (e.target.value) {
                      params.set('hddt_status', e.target.value);
                    } else {
                      params.delete('hddt_status');
                    }
                    navigate(`?${params.toString()}`, { replace: true });
                  }}
                  label="Hóa đơn điện tử"
                  displayEmpty
                >
                  <MenuItem value="">Tất cả</MenuItem>
                  <MenuItem value="none">Chưa có HDDT</MenuItem>
                  <MenuItem value="draft">HDDT nháp</MenuItem>
                  <MenuItem value="signed">HDDT đã ký</MenuItem>
                  <MenuItem value="adjusted">HDDT điều chỉnh</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}> {/* Added margin top */}
              <Button
                onClick={resetAllFilters}
                variant="text"
                color="secondary" // Added color for consistency
              >
                Xóa bộ lọc
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>

      {
        isMobile ? (
          <Stack spacing={2}>
            {displayedOrders.map((order, index) => (
              <Card key={index} onClick={() => navigate(`/orders/${order.id}`)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" gutterBottom>{order.user.name}</Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                      {formatDate(order.processed_at)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Số điện thoại: {order.user.phone}</Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>Mã đơn: {order.name}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {order.order_type === 'INSTANT_ORDER' ? (
                      <Chip label="Đơn lẻ" size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }} />
                    ) : order.order_type === 'MONTHLY_PACKAGE_ORDER' ? (
                      <Chip label="Đơn tháng" size="small" sx={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }} />
                    ) : null}
                    {order.status === 'CONFIRMING' ? (
                      <Chip label="Chờ xác nhận" size="small" sx={{ backgroundColor: '#fff8e1', color: '#f57f17' }} />
                    ) : order.status === 'OPEN' ? (
                      <Chip label="Mới" size="small" sx={{ backgroundColor: '#fff3e0', color: '#e65100' }} />
                    ) : order.status === 'PACKAGED' ? (
                      <Chip label="Đã đóng gói" size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }} />
                    ) : order.status === 'CANCELLED' ? (
                      <Chip label="Đã hủy" size="small" sx={{ backgroundColor: '#f5f5f5', color: '#757575' }} />
                    ) : order.status === 'SENT_INVOICE' ? (
                      <Chip label="Đã gửi hóa đơn" size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                    ) : null}
                    {order.financial_status === 'UNPAID' ? (
                      <Chip label="Chưa thanh toán" size="small" sx={{ backgroundColor: '#ffebee', color: '#c62828' }} />
                    ) : order.financial_status === 'PAID' ? (
                      <Chip label="Đã thanh toán" size="small" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                    ) : order.financial_status === 'SKIPPED' ? (
                      <Chip label="Bỏ qua" size="small" sx={{ backgroundColor: '#f5f5f5', color: '#757575' }} />
                    ) : order.financial_status === 'MAPPED_BANK_TRANSACTION' ? (
                      <Chip label="Đã ghép GD ngân hàng" size="small" sx={{ backgroundColor: '#e3f2fd', color: '#0097a7' }} />
                    ) : null}
                    <HddtChip status={order.hddt_status} size="small" />
                  </Box>
                </CardContent >
              </Card >
            ))}
            <Paper sx={{ position: 'sticky', bottom: 0, left: 0, right: 0, zIndex: 1 }}>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={totalOrders}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Số hàng mỗi trang"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} của ${count}`}
              />
            </Paper>
          </Stack >
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Checkbox
                      checked={selectAll}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Ngày xử lý</TableCell>
                  <TableCell>Tên khách hàng</TableCell>
                  <TableCell>Số điện thoại</TableCell>
                  <TableCell>Mã đơn hàng</TableCell>
                  <TableCell>Loại đơn</TableCell>
                  <TableCell>Địa chỉ</TableCell>
                  <TableCell>Ghi chú</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell>HDDT</TableCell>
                  <TableCell>Thanh toán</TableCell>
                  <TableCell>Tạo bởi</TableCell>
                </TableRow >
              </TableHead >
              <TableBody>
                {displayedOrders.map((order, index) => (
                  <TableRow
                    key={index}
                    onClick={() => navigate(`/orders/${order.id}`)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedOrders.includes(order.id)}
                        onChange={() => handleSelectOrder(order.id)}
                      />
                    </TableCell>
                    <TableCell>{formatDate(order.processed_at)}</TableCell>
                    <TableCell>{order.user.name}</TableCell>
                    <TableCell>{order.phone_number}</TableCell>
                    <TableCell
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        cursor: 'text',
                      }}
                    >
                      {order.name}
                    </TableCell>
                    <TableCell>
                      {order.order_type === 'INSTANT_ORDER' ? (
                        <Chip label="Đơn lẻ" sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }} />
                      ) : order.order_type === 'MONTHLY_PACKAGE_ORDER' ? (
                        <Chip label="Đơn tháng" sx={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }} />
                      ) : (
                        <Chip label="N/A" />
                      )}
                    </TableCell>
                    <TableCell>{order.shipping_address}</TableCell>
                    <TableCell>{order.note}</TableCell>
                    <TableCell>
                      {order.tags ? (
                        order.tags.split(',').map((tag, tagIndex) => (
                          <Chip
                            key={tagIndex}
                            label={tag.trim()}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {order.status === 'CONFIRMING' ? (
                        <Chip label="Chờ xác nhận" sx={{ backgroundColor: '#fff8e1', color: '#f57f17' }} />
                      ) : order.status === 'OPEN' ? (
                        <Chip label="Mới" sx={{ backgroundColor: '#fff3e0', color: '#e65100' }} />
                      ) : order.status === 'PACKAGED' ? (
                        <Chip label="Đã đóng gói" sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }} />
                      ) : order.status === 'CANCELLED' ? (
                        <Chip label="Đã hủy" sx={{ backgroundColor: '#f5f5f5', color: '#757575' }} />
                      ) : order.status === 'SENT_INVOICE' ? (
                        <Chip label="Đã gửi hóa đơn" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                      ) : (
                        <Chip label={order.status} sx={{ backgroundColor: '#f5f5f5', color: '#757575' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {order.hddt_status && order.hddt_status !== 'none'
                        ? <HddtChip status={order.hddt_status} />
                        : <Chip label="Chưa có" sx={{ backgroundColor: '#f5f5f5', color: '#9e9e9e' }} />}
                    </TableCell>
                    <TableCell>
                      {order.financial_status === 'UNPAID' ? (
                        <Chip label="Chưa thanh toán" sx={{ backgroundColor: '#ffebee', color: '#c62828' }} />
                      ) : order.financial_status === 'PAID' ? (
                        <Chip label="Đã thanh toán" sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }} />
                      ) : order.financial_status === 'SKIPPED' ? (
                        <Chip label="Bỏ qua" sx={{ backgroundColor: '#f5f5f5', color: '#757575' }} />
                      ) : order.financial_status === 'MAPPED_BANK_TRANSACTION' ? (
                        <Chip label="Đã ghép GD ngân hàng" sx={{ backgroundColor: '#e3f2fd', color: '#0097a7' }} />
                      ) : null}
                    </TableCell>
                    <TableCell>{order.created_by_name}</TableCell>
                  </TableRow >
                ))}
              </TableBody >
            </Table >
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalOrders}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Số hàng mỗi trang"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} của ${count}`}
            />
          </TableContainer >
        )
      }
    </Container >
  );
};

export default ListOrders;
